import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Card, IconButton, InputAdornment, Pagination, Switch, Table, TableBody, TableContainer, TablePagination, TextField, Typography, Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { getAllCustomerStore, deleteCustomerStore, restoreCustomerStore } from 'store/customer';
import { useTableState } from 'hooks/useTableState';
import { StyledTableCell, StyledTableRow } from 'commons/generics/StyledTableComponents';
import { GenericTableHead } from 'commons/generics/GenericTableHead';
import { SinDatos } from 'commons/generics/SinDatos';
import { corregirTexto, firstLetterUpperCase, hasPemissions, usarFuncionesComposicion } from 'commons/utils';
import { paddingCardsTables } from 'commons';
import { ModalConfirmDeactivation } from 'components/modals/ModalConfirmDeactivation';
import { ModalConfirmRestore } from 'components/modals/ModalConfirmRestore';


export const TableCustomer = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { customers_list }: any = useSelector((state: any) => state.customer);

    const [hasPermission, setHasPermission] = useState(true);
    const [canView, setCanView] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [canRestore, setCanRestore] = useState(false);

    const {
        dataTableList,
        openElem, setOpenElem,
        checked, setChecked,
        searchTerm, setSearchTerm,
        order, orderBy,
        handleSwitchChange,
        handleSearch: handleSearchHook,
        handleResetSearch: handleResetSearchHook,
        page, rowsPerPage,
        handleChangePage, handleChangeRowsPerPage,
        pagePagination, itemCountPagination,
        changePaginationPage: changePaginationPageHook,
        handleRequestSort,
        updatePaginationFromList,
        openModalDeactivation, setOpenModalDeactivation,
        openModalRestore, setOpenModalRestore,
        handleResponse,
        getSortedAndFilteredList,
        isServerSide
    } = useTableState('customers_table');

    const [inputValue, setInputValue] = useState('');

    const checkPermissions = async () => {
        const view = await hasPemissions('modulo.manager', 'modulo.cliente', 'view');
        if (!view) setHasPermission(false);
        else setCanView(true);

        const edit = await hasPemissions('modulo.manager', 'modulo.cliente', 'edit');
        if (edit) setCanEdit(true);

        const del = await hasPemissions('modulo.manager', 'modulo.cliente', 'delete');
        if (del) {
            setCanDelete(true);
            setCanRestore(true);
        }
    };

    useEffect(() => {
        checkPermissions();
    }, []);

    const getInitialData = async (statusVal = checked ? 0 : 1) => {
        const response = await dispatch(getAllCustomerStore({ page: 1, take: rowsPerPage, status: statusVal, param: '' }));
        await handleResponse(response, 'obtener lista de clientes', false);
    };

    useEffect(() => {
        if (canView) {
            getInitialData();
        }
    }, [canView]);

    useEffect(() => {
        updatePaginationFromList(customers_list);
    }, [customers_list, updatePaginationFromList]);

    const handleSearch = async (param: string) => {
        setSearchTerm(param);
        setInputValue(param);
        handleSearchHook(param, dispatch, (p: string) => getAllCustomerStore({ page: 1, take: rowsPerPage, status: checked ? 0 : 1, param: p }), getInitialData, null, 'buscar clientes');
    };

    const onSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        handleSwitchChange(e);
        const newChecked = e.target.checked;
        const status = newChecked ? 0 : 1;
        
        if (searchTerm) {
            handleSearchHook(searchTerm, dispatch, (p: string) => getAllCustomerStore({ page: 1, take: rowsPerPage, status, param: p }), () => getInitialData(status), null, 'buscar clientes');
        } else {
            getInitialData(status);
        }
    };

    const onResetSearch = () => {
        setInputValue('');
        handleResetSearchHook(() => getInitialData(checked ? 0 : 1));
    };

    const changePaginationPage = async (event: React.ChangeEvent<unknown>, value: number) => {
        const validPage = value < 1 ? 1 : value;
        changePaginationPageHook(event, validPage);
        const response = await dispatch(getAllCustomerStore({ page: validPage, take: rowsPerPage, status: checked ? 0 : 1, param: searchTerm }));
        await handleResponse(response, 'listar clientes', false);
    };

    const confirmDeactivate = async () => {
        if (!openElem) return;
        const response = await dispatch(deleteCustomerStore(openElem.id, { page: pagePagination, take: rowsPerPage, status: checked ? 0 : 1, param: searchTerm }));
        await handleResponse(response, 'desactivar cliente', true);
    };

    const confirmRestore = async () => {
        if (!openElem) return;
        const response = await dispatch(restoreCustomerStore(openElem.id, { page: pagePagination, take: rowsPerPage, status: checked ? 0 : 1, param: searchTerm }));
        await handleResponse(response, 'restaurar cliente', true);
    };

    const handleView = (row: any) => {
         navigate(`/manager/clientes/${row.id}/detalle`);
    }
    const handleEdit = (row: any) => {
         navigate(`/manager/clientes/${row.id}/editar`);
    }

    const headCells = [
        { id: 'index', numeric: false, disablePadding: true, label: '#' },
        { id: 'name', numeric: false, disablePadding: true, label: 'Nombre' },
        { id: 'email', numeric: false, disablePadding: false, label: 'Email' },
        { id: 'person_in_charge', numeric: false, disablePadding: false, label: 'Nombre contacto' },
        { id: 'contact', numeric: false, disablePadding: false, label: 'Numero Contacto' },
        { id: 'status', numeric: false, disablePadding: false, label: 'Estado' },
        { id: 'actions', numeric: false, disablePadding: false, label: 'Acciones' },
    ];

    const sortedList = getSortedAndFilteredList(
        dataTableList || [], 
        ['name', 'email', 'person_in_charge', 'contact'], 
        'status', 
        checked ? 0 : 1 
    );

    if (!hasPermission) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="textSecondary">No tienes permisos para ver este contenido.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', mt: paddingCardsTables }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
                <TextField
                    placeholder="Buscar..."
                    variant="outlined"
                    size="small"
                    autoComplete='off'
                    value={inputValue}
                    onChange={(e) => handleSearch(e.target.value)}
                    sx={{ width: 400 }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                        endAdornment: inputValue && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={onResetSearch}><ClearIcon /></IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Switch checked={checked} onChange={onSwitchChange} color="primary" />
                    <Typography variant="body2" sx={{ color: '#4B5563', fontWeight: 500 }}>
                        Mostrar solo inactivos
                    </Typography>
                </Box>  
            </Box>

            <Card sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none', overflow: 'hidden' }}>
                <TableContainer>
                    <Table>
                        <GenericTableHead 
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            headCells={headCells}
                        />
                        <TableBody>
                            {sortedList.length > 0 ? (
                                sortedList
                                    .slice(isServerSide ? 0 : page * rowsPerPage, isServerSide ? rowsPerPage : page * rowsPerPage + rowsPerPage)
                                    .map((row: any, index: number) => {
                                        
                                        const currentIndex = isServerSide ? (pagePagination - 1) * 10 + index + 1 : index + 1;

                                        return (
                                            <StyledTableRow key={row.id}>
                                                <StyledTableCell component="th" scope="row">{currentIndex}</StyledTableCell>
                                                <StyledTableCell>
                                                    {usarFuncionesComposicion(corregirTexto, firstLetterUpperCase, row.name)}
                                                </StyledTableCell>
                                                <StyledTableCell sx={{ color: 'gray'}}>
                                                    {row.email}
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {usarFuncionesComposicion(corregirTexto, firstLetterUpperCase, row.person_in_charge)}
                                                </StyledTableCell>
                                                <StyledTableCell>{row.contact}</StyledTableCell>
                                                <StyledTableCell>
                                                    {row.status === 0 && <Chip size='small' label="Inactivo" sx={{ color: '#FF4C51', background: '#FFE2E3', fontWeight: 700 }} />}
                                                    {row.status === 1 && <Chip size='small' label="Activo" sx={{ color: '#5DCC0A', background: '#E4F6D6', fontWeight: 700 }} />}
                                                    {row.status === 2 && <Chip size='small' label="Vencidos" sx={{ color: '#FFB911', background: '#FFF3D6', fontWeight: 700 }} />}
                                                </StyledTableCell>
                                                
                                                <StyledTableCell align="center"> 
                                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                                        <span style={{ cursor: !canView ? 'not-allowed' : 'pointer' }}>
                                                            <IconButton size="small" onClick={() => canView && handleView(row)} disabled={!canView} color="info">
                                                                <VisibilityOutlinedIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                        
                                                        <span style={{ cursor: !canEdit ? 'not-allowed' : 'pointer' }}>
                                                            <IconButton size="small" onClick={() => canEdit && handleEdit(row)} disabled={!canEdit} color="primary">
                                                                <EditOutlinedIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                        
                                                        {row.status !== 0 ? (
                                                            <span style={{ cursor: !canDelete ? 'not-allowed' : 'pointer' }}>
                                                                <IconButton size="small" onClick={() => { if(canDelete){ setOpenElem(row); setOpenModalDeactivation(true); } }} disabled={!canDelete} color="error">
                                                                    <DeleteOutlineOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        ) : (
                                                            <span style={{ cursor: !canRestore ? 'not-allowed' : 'pointer' }}>
                                                                <IconButton size="small" onClick={() => { if(canRestore){ setOpenElem(row); setOpenModalRestore(true); } }} disabled={!canRestore} color="success">
                                                                    <RestoreOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        )}
                                                    </Box>
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        );
                                    })
                            ) : (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={7} sx={{ p: 0 }}>
                                        <SinDatos />
                                    </StyledTableCell>
                                </StyledTableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            {dataTableList.length > 0 && (
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={customers_list?.meta?.itemCount || dataTableList.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Filas por página"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
            )}

            {dataTableList.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, padding: 2.5, borderTop: 1, borderColor: 'divider' }}>
                    <Pagination
                        sx={{ justifyContent: 'center' }}
                        color="primary"
                        count={itemCountPagination}
                        page={pagePagination}
                        onChange={changePaginationPage} 
                        showFirstButton 
                        showLastButton
                        shape="circular"
                    />
                </Box>
            )}

            <ModalConfirmDeactivation
                open={openModalDeactivation} 
                setOpen={setOpenModalDeactivation} 
                title='¿Estás seguro que deseas desactivar este cliente?'
                description='Este cliente pasará a estado inactivo.'
                onConfirm={confirmDeactivate}
            />
            <ModalConfirmRestore
                open={openModalRestore}
                setOpen={setOpenModalRestore}
                title='¿Estás seguro que deseas restaurar este cliente?'
                description='Este cliente pasará a estado activo.'
                onConfirm={confirmRestore}
            />
        </Box>
    );
};
